/**
 * A number a report row publishes — or the reason there is no number.
 *
 * ── Why this is a type and not a `??` ───────────────────────────────────────
 *
 * D-22 shipped a withheld overtime figure as **"0 giờ"** on an employee's own
 * screen. Task 46 answered that inside the generated file with four sentinels that
 * are text and never numeric cells. This module is the same answer one level up, on
 * the screen that *requests* the file.
 *
 * `Report.row_count`, `Report.size_bytes` and `scope_snapshot.employee_count` are all
 * `integer | null`. Every one of them has a legitimate `0` — an export really can
 * match no rows — so "falsy means missing" is wrong here in the most dangerous
 * possible way. The union below makes the confusion **impossible to write** rather
 * than merely discouraged: the `absent` variant carries no numeric member at all, so
 * a renderer reaching for `.value` on an absence does not compile, and a component
 * that wanted to print `0` for one would have to invent the number itself.
 *
 * That is the structural half the brief asks for — *the type must make it
 * impossible, not the rendering*. `web/src/incidents/sla.ts` did the same thing for a
 * breached deadline, whose variant carries `overdueMs` and deliberately no
 * `remainingMs`, so no renderer can draw a countdown that should not exist.
 *
 * ── The four absences are not one absence ───────────────────────────────────
 *
 * They are `src/modules/reports/types.ts`'s own `ABSENCE_KINDS`, and they are kept
 * apart here for the reason the standing constraints give about `shiftFor` and the
 * session states: a human is asked to explain the difference between them. A screen
 * that folded them into "—" would be re-introducing exactly the collapse the file
 * format spends four sentinels avoiding.
 *
 * The strings are duplicated from that module rather than imported, because
 * `web/dist` is static JavaScript served to browsers and a `web/ → src/modules/**`
 * import is a precedent whose second use brings a repository or a `pg` handle into a
 * public bundle. `figure.test.ts` parses that file at test time and compares the two
 * as **sets, in both directions**, so a fifth sentinel added on the server turns this
 * project red on the day it is added.
 */

import type { Report, ReportStatusName } from '../api/types';

/** `ABSENCE_KINDS` of `src/modules/reports/types.ts`, in document order. */
export const ABSENCE_KINDS = [
  'not_computed',
  'withheld_private',
  'data_incomplete',
  'not_applicable',
] as const;

export type AbsenceKind = (typeof ABSENCE_KINDS)[number];

/**
 * `ABSENCE_TEXT` of the same module — the literal a cell prints in every format.
 *
 * Rendered on screen verbatim, `#` and all, and not translated: an HR administrator
 * who meets `#WITHHELD_PRIVATE` in a spreadsheet needs the page's legend to use the
 * same eight characters the file does, or the legend is about a different thing.
 */
export const ABSENCE_TEXT: Readonly<Record<AbsenceKind, string>> = {
  not_computed: '#NOT_COMPUTED',
  withheld_private: '#WITHHELD_PRIVATE',
  data_incomplete: '#DATA_INCOMPLETE',
  not_applicable: '#NOT_APPLICABLE',
};

/** A measured number, or a named absence carrying no number to misread. */
export type ReportFigure =
  | { readonly kind: 'measured'; readonly value: number }
  | { readonly kind: 'absent'; readonly absence: AbsenceKind };

export function measured(value: number): ReportFigure {
  return { kind: 'measured', value };
}

export function absent(absence: AbsenceKind): ReportFigure {
  return { kind: 'absent', absence };
}

/**
 * Why a report row has no figure, decided by its **status** rather than by a guess.
 *
 * Each branch is a different sentence to the person waiting for a file:
 *
 *  - `pending` / `running` → **`#NOT_COMPUTED`.** Nothing has been counted yet. This
 *    is the branch a `?? 0` would turn into "your export found nothing", which is the
 *    single worst thing this screen could say to somebody who is still waiting.
 *  - `failed` → **`#NOT_APPLICABLE`.** No file was produced, so a row count is not a
 *    missing measurement — it is a column with no meaning for this row.
 *  - `done` / `expired` → **`#DATA_INCOMPLETE`.** A file was generated and the
 *    generator recorded no figure for it. The export is not in doubt; the *record* of
 *    it is, and saying so is how somebody knows to check the file itself.
 *
 * `withheld_private` is deliberately not produced here and is not dead: it is the
 * sentinel for a value the **file** withholds by policy, which is a cell-level fact
 * inside the generated artefact and never a property of the `wp_reports` row. The
 * legend on screen still names it, because the reader will meet it in the file.
 */
export function absenceForStatus(status: ReportStatusName): AbsenceKind {
  if (status === 'pending' || status === 'running') return 'not_computed';
  if (status === 'failed') return 'not_applicable';
  return 'data_incomplete';
}

/**
 * A `number | null | undefined` from a report row, read against that row's status.
 *
 * `Number.isFinite` rather than a truthiness test, and that is the whole point:
 * `0` is a **measured** zero here — an export that matched no rows really did match
 * no rows — and it must survive as one. `NaN` and `Infinity` cannot arrive over JSON
 * from this server, and are treated as an absence rather than printed, because a
 * screen that renders `NaN dòng` has told the reader nothing at all.
 */
export function figureOf(value: number | null | undefined, status: ReportStatusName): ReportFigure {
  if (typeof value === 'number' && Number.isFinite(value)) return measured(value);
  return absent(absenceForStatus(status));
}

export function rowCountFigure(report: Report): ReportFigure {
  return figureOf(report.row_count, report.status);
}

export function sizeFigure(report: Report): ReportFigure {
  return figureOf(report.size_bytes, report.status);
}

/**
 * How many employees the frozen scope covered — and the case where no count exists.
 *
 * `openapi.yaml` is explicit: `employee_count` is `null` when the scope was **the
 * whole company**, "an open set that grows with every hire, which no count
 * describes". That is `#NOT_APPLICABLE` — the column has no meaning for this row —
 * and it is emphatically **not** `0`, which would read as an export covering nobody.
 *
 * A `scope_snapshot` that is absent entirely, or that carries no `employee_count`
 * member, is a different fact again: the server published no scope, so nothing was
 * computed. Two absences, kept apart, because "we did not measure this" and "there is
 * no number to measure" are not the same answer to an auditor asking who was in the
 * export.
 */
export function scopeEmployeeFigure(report: Report): ReportFigure {
  const snapshot = report.scope_snapshot;
  if (snapshot === undefined) return absent('not_computed');
  const count = snapshot.employee_count;
  if (count === null) return absent('not_applicable');
  if (typeof count === 'number' && Number.isFinite(count)) return measured(count);
  return absent('not_computed');
}
